<template>
    <div class="search-query-group">
        <q-expansion-item v-model="expanded"
                          :header-class="'search-query-group-header'"
                          default-opened
                          class="search-query-group-expansion">
            <template #header>
                <span class="search-query-group-header-inner">
                    <span class="search-query-drag-handle column-drag-handle"
                          aria-label="Drag to reorder"
                          @click.stop>
                        <q-icon name="mdi-drag" size="xs" />
                    </span>
                    <span class="search-query-group-label"> Match</span>
                    <span class="search-query-operator-wrap" @click.stop>
                        <q-select v-model="group.operator"
                                  :options="operatorOptions"
                                  dense
                                  outlined
                                  hide-bottom-space
                                  emit-value
                                  map-options
                                  options-dense
                                  class="search-query-operator-select q-mx-xs"
                                  aria-label="Group operator"
                                  @update:model-value="$emit('change')" />
                    </span>
                    <span class="search-query-group-label">of the following:</span>
                    <q-btn flat
                           round
                           dense
                           size="sm"
                           icon="mdi-close"
                           aria-label="Remove group"
                           class="q-ml-xs"
                           @click.stop="removeGroup">
                        <q-tooltip>Remove group</q-tooltip>
                    </q-btn>
                </span>
            </template>

            <div class="search-query-group-body">
                <Container class="search-query-container"
                           :group-name="smoothDndGroupName"
                           :get-child-payload="getChildPayload"
                           :get-ghost-parent="getGhostParent"
                           :drag-handle-selector="'.column-drag-handle'"
                           @drop="onDrop">
                    <template v-if="group.children && group.children.length > 0">
                        <Draggable v-for="(child, index) in group.children"
                                   :key="child.id"
                                   class="search-query-draggable">
                            <!-- Term row: Field + Operator + Value by type -->
                            <div v-if="child.type === 'term'"
                                 class="search-query-term-row row items-center q-mb-sm">
                                <span class="search-query-drag-handle column-drag-handle"
                                      aria-label="Drag to reorder">
                                    <q-icon name="mdi-drag" size="xs" />
                                </span>
                                <q-select v-model="child.field"
                                          :options="filteredFieldOptions"
                                          use-input
                                          fill-input
                                          hide-selected
                                          option-label="label"
                                          dense
                                          outlined
                                          hide-bottom-space
                                          placeholder="Select field"
                                          class="search-query-field-select"
                                          style="min-width: 110px;"
                                          aria-label="Field"
                                          @update:model-value="(v) => onFieldChange(child, v)"
                                          @filter="filterFields" />
                                <q-select v-if="child.field && getOperatorOptionsForType(child.field?.type).length > 0"
                                          v-model="child.operator"
                                          :options="getOperatorOptionsForType(child.field?.type)"
                                          dense
                                          outlined
                                          hide-bottom-space
                                          emit-value
                                          map-options
                                          options-dense
                                          class="search-query-operator-term"
                                          style="min-width: 95px;"
                                          aria-label="Operator"
                                          @update:model-value="(v) => onOperatorChange(child, v)" />
                                <!-- Value: text -->
                                <template v-if="child.field && child.field?.type === 'text'">
                                    <q-input v-model="child.value"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="Value"
                                             class="col search-query-value-input"
                                             aria-label="Value"
                                             @update:model-value="$emit('change')" />
                                    <q-checkbox v-model="child.caseInsensitive"
                                                dense
                                                class="q-ml-sm"
                                                aria-label="Case insensitive"
                                                @update:model-value="$emit('change')">
                                        <q-tooltip>Case Insensitive</q-tooltip>
                                    </q-checkbox>
                                </template>
                                <!-- Value: numeric single -->
                                <template v-else-if="child.field && child.field?.type === 'numeric' && child.operator !== 'range'">
                                    <q-input v-model.number="child.value"
                                             type="number"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="Value"
                                             class="search-query-value-input"
                                             style="min-width: 72px;"
                                             aria-label="Value"
                                             @update:model-value="$emit('change')" />
                                </template>
                                <!-- Value: numeric range -->
                                <template v-else-if="child.field && child.field?.type === 'numeric' && child.operator === 'range'">
                                    <q-input :model-value="Array.isArray(child.value) ? child.value[0] : null"
                                             type="number"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="Min"
                                             class="search-query-value-input"
                                             style="min-width: 68px;"
                                             aria-label="Min"
                                             @update:model-value="(v) => setRangeValue(child, 0, v)" />
                                    <q-input :model-value="Array.isArray(child.value) ? child.value[1] : null"
                                             type="number"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="Max"
                                             class="search-query-value-input"
                                             style="min-width: 68px;"
                                             aria-label="Max"
                                             @update:model-value="(v) => setRangeValue(child, 1, v)" />
                                </template>
                                <!-- Value: date single -->
                                <template v-else-if="child.field && child.field?.type === 'date' && child.operator !== 'range'">
                                    <q-input v-model="child.value"
                                             type="date"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             class="search-query-value-input"
                                             style="min-width: 110px;"
                                             aria-label="Value"
                                             @update:model-value="$emit('change')" />
                                </template>
                                <!-- Value: date range -->
                                <template v-else-if="child.field && child.field?.type === 'date' && child.operator === 'range'">
                                    <q-input :model-value="Array.isArray(child.value) ? child.value[0] : ''"
                                             type="date"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="From"
                                             class="search-query-value-input"
                                             style="min-width: 100px;"
                                             aria-label="From"
                                             @update:model-value="(v) => setRangeValue(child, 0, v)" />
                                    <q-input :model-value="Array.isArray(child.value) ? child.value[1] : ''"
                                             type="date"
                                             dense
                                             outlined
                                             hide-bottom-space
                                             placeholder="To"
                                             class="search-query-value-input"
                                             style="min-width: 100px;"
                                             aria-label="To"
                                             @update:model-value="(v) => setRangeValue(child, 1, v)" />
                                </template>
                                <!-- Value: boolean -->
                                <template v-else-if="child.field && child.field?.type === 'boolean'">
                                    <q-select v-model="child.value"
                                              :options="booleanValueOptions"
                                              dense
                                              outlined
                                              hide-bottom-space
                                              emit-value
                                              map-options
                                              options-dense
                                              class="search-query-value-input"
                                              style="min-width: 80px;"
                                              aria-label="Value"
                                              @update:model-value="$emit('change')" />
                                </template>
                                <q-btn flat
                                       round
                                       dense
                                       size="sm"
                                       icon="mdi-close"
                                       aria-label="Remove term"
                                       @click="removeChild(index)">
                                    <q-tooltip>Remove term</q-tooltip>
                                </q-btn>
                            </div>
                            <!-- Nested group -->
                            <SearchQueryGroup v-else-if="child.type === 'group'"
                                              :group="child"
                                              :depth="depth + 1"
                                              :parent-children="group.children"
                                              :self-index="index"
                                              :root-operator="group.operator"
                                              @change="$emit('change')" />
                            <div v-if="index < group.children.length - 1"
                                 class="search-query-operator-separator">
                                {{ group.operator === 'or' ? '- OR -' : '- AND -' }}
                            </div>
                        </Draggable>
                    </template>
                    <div v-else class="search-query-drop-placeholder">Drop here</div>
                </Container>
                <div v-if="!(group.children && group.children.length > 0)" class="search-query-empty-hint q-mb-sm text-grey-7">No terms yet. Add a term or group below.</div>
            </div>

            <div class="row q-gutter-sm q-mt-sm">
                <q-btn size="sm"
                       icon="mdi-plus"
                       color="primary"
                       outline
                       aria-label="Add term"
                       @click="addTerm">
                    <q-tooltip>Add term</q-tooltip>
                    Add term
                </q-btn>
                <q-btn size="sm"
                       icon="mdi-plus"
                       color="primary"
                       outline
                       aria-label="Add group"
                       @click="addGroup">
                    <q-tooltip>Add group</q-tooltip>
                    Add group
                </q-btn>
            </div>
        </q-expansion-item>
    </div>
</template>

<script>
import { Container, Draggable } from 'vue3-smooth-dnd';
import { QExpansionItem, QSelect, QBtn, QIcon, QInput, QCheckbox, QTooltip } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import {
  SMOOTH_DND_GROUP_NAME,
  MOCK_FIELD_OPTIONS,
  BOOLEAN_VALUE_OPTIONS,
  getOperatorOptionsForType as getOperatorOptions,
  getDefaultOperatorForType,
  getDefaultValueForTerm,
} from './searchQueryBuilderFields.js';

function createTerm() {
  return {
    type: 'term',
    field: null,
    operator: 'partial',
    value: '',
    caseInsensitive: false,
    id: utils.generateUUID(),
  };
}

function createGroup() {
  return {
    type: 'group',
    operator: 'and',
    children: [],
    id: utils.generateUUID(),
  };
}

export default {
  name: 'SearchQueryGroup',
  components: {
    Container,
    Draggable,
    QExpansionItem,
    QSelect,
    QBtn,
    QIcon,
    QInput,
    QCheckbox,
    QTooltip,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    parentChildren: {
      type: Array,
      default: null,
    },
    selfIndex: {
      type: Number,
      default: -1,
    },
    rootOperator: {
      type: String,
      default: 'and',
      validator: (v) => v === 'and' || v === 'or',
    },
  },
  emits: ['change'],
  computed: {
    rootOperatorLabel() {
      return this.rootOperator === 'or' ? 'Or' : 'And';
    },
    smoothDndGroupName() {
      return SMOOTH_DND_GROUP_NAME;
    },
  },
  data() {
    return {
      expanded: true,
      operatorOptions: [
        { value: 'and', label: 'All' },
        { value: 'or', label: 'Any' },
      ],
      fieldOptions: MOCK_FIELD_OPTIONS,
      filteredFieldOptions: [...MOCK_FIELD_OPTIONS],
      booleanValueOptions: BOOLEAN_VALUE_OPTIONS,
    };
  },
  methods: {
    getOperatorOptionsForType(fieldType) {
      return getOperatorOptions(fieldType);
    },
    filterFields(val, update) {
      update(() => {
        if (val === '') {
          this.filteredFieldOptions = [...this.fieldOptions];
        } else {
          const needle = val.toLowerCase();
          this.filteredFieldOptions = this.fieldOptions.filter(
            (f) => f.label.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    },
    onFieldChange(term, field) {
      term.field = field || null;
      if (field) {
        term.operator = getDefaultOperatorForType(field.type);
        term.value = getDefaultValueForTerm(field.type, term.operator);
      }
      this.$emit('change');
    },
    onOperatorChange(term, operator) {
      const prevRange = term.operator === 'range';
      const nextRange = operator === 'range';
      term.operator = operator;
      if (!prevRange && nextRange) {
        const single = term.value;
        term.value = term.field?.type === 'date' ? [single || '', single || ''] : [single, single];
      } else if (prevRange && !nextRange) {
        term.value = Array.isArray(term.value) ? term.value[0] : getDefaultValueForTerm(term.field?.type, operator);
      }
      this.$emit('change');
    },
    setRangeValue(term, index, v) {
      if (!Array.isArray(term.value)) term.value = [null, null];
      const arr = [...term.value];
      arr[index] = term.field?.type === 'numeric' ? (v === '' || v == null ? null : Number(v)) : v;
      term.value = arr;
      this.$emit('change');
    },
    getChildPayload(index) {
      return this.group.children && this.group.children[index];
    },
    getGhostParent() {
      return typeof document !== 'undefined' ? document.body : null;
    },
    onDrop(result) {
      if (result.removedIndex == null && result.addedIndex == null) return;
      if (!this.group.children) this.group.children = [];
      const children = this.group.children;
      let itemToAdd = result.payload;
      if (result.removedIndex !== null) {
        itemToAdd = children.splice(result.removedIndex, 1)[0];
      }
      if (result.addedIndex !== null && itemToAdd != null) {
        children.splice(result.addedIndex, 0, itemToAdd);
      }
      this.$emit('change');
    },
    removeChild(index) {
      this.group.children.splice(index, 1);
      this.$emit('change');
    },
    removeGroup() {
      if (this.parentChildren != null && this.selfIndex >= 0) {
        this.parentChildren.splice(this.selfIndex, 1);
        this.$emit('change');
      }
    },
    addTerm() {
      if (!this.group.children) this.group.children = [];
      this.group.children.push(createTerm());
      this.$emit('change');
    },
    addGroup() {
      if (!this.group.children) this.group.children = [];
      this.group.children.push(createGroup());
      this.$emit('change');
    },
  },
};
</script>

<style scoped>
    .search-query-group {
        margin-bottom: 4px;
        position: relative;
    }

        .search-query-group :deep(.q-expansion-item__container) {
            padding-left: 0;
        }

        .search-query-group :deep(.search-query-group-header) {
            padding-left: 0 !important;
        }

    .search-query-group-body {
        position: relative;
        padding-left: 20px;
    }

        .search-query-group-body::before {
            content: '';
            position: absolute;
            left: 8px;
            top: 0;
            bottom: 0;
            width: 1px;
            background: rgba(0, 0, 0, 0.2);
            pointer-events: none;
        }

    .search-query-group-header-inner {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
    }

    .search-query-drag-handle,
    .column-drag-handle {
        cursor: grab;
        padding: 2px 4px;
        user-select: none;
    }

        .search-query-drag-handle:active,
        .column-drag-handle:active {
            cursor: grabbing;
        }

    .search-query-group-label {
        font-weight: 500;
        white-space: nowrap;
    }

    .search-query-operator-select {
        min-width: 80px;
        max-width: 100px;
    }

    .search-query-term-row {
        gap: 6px;
        flex-wrap: wrap;
    }

    .search-query-field-select,
    .search-query-operator-term {
        flex-shrink: 0;
    }

    .search-query-value-input {
        min-width: 60px;
        flex: 1;
    }

    .search-query-group :deep(.q-field) {
        font-size: 0.875rem;
    }

    .search-query-group :deep(.q-field__control-container) {
        min-height: 32px;
    }

    .search-query-group :deep(.q-field .q-field__control) {
        min-height: 32px;
    }

    .search-query-container {
        min-height: 4px;
    }

    .search-query-drop-placeholder {
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px dashed rgba(0, 0, 0, 0.3);
        border-radius: 8px;
        color: rgba(0, 0, 0, 0.45);
        font-size: 0.9rem;
        padding: 8px;
    }

    .search-query-draggable {
        margin-bottom: 4px;
    }

    .search-query-operator-separator {
        font-size: 0.7rem;
        color: rgba(0, 0, 0, 1);
        text-align: center;
        padding: 2px 0;
        line-height: 1.2;
        font-weight: bold;
    }

    .search-query-empty-hint {
        font-size: 0.85rem;
    }
</style>
